import React, { useState, useMemo, useEffect } from 'react';

import { dashboardDeliveryRequestApis } from '@apis/next/deliveryRequestApis';
import { dashboardInvoiceApis } from '@apis/next/invoiceApis';
import { overviewApis } from '@apis/next/overview';
import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import { dashboardVesselScheduleApis } from '@apis/next/vesselScheduleApis';
import { workdayApis } from '@apis/next/workday';
import { CircularProgressBar } from '@components/admin/dashboard/CircularProgressBar';
import { SmallCalendar } from '@components/admin/dashboard/SmallCalendar';
import { Layout } from '@components/admin/Layout';
import { SectionTitle } from '@components/admin/SectionTitle';
import { QueryContainer } from '@components/QueryContainer';
import { d2dServices, dashboardStrings, kaijoServices } from '@data/dashboard';
import { useUser } from '@hooks/queries/useUser';
import { CustomsStatusName } from '@models/next/CustomsRequestStatus';
import { DOStatusName } from '@models/next/DOStatus';
import { InstructionStatusName } from '@models/next/Instruction';
import { MoveOutStatusName } from '@models/next/MoveOutStatus';
import { type Overview } from '@models/next/Overview';
import { PreAlertType } from '@models/next/PreAlert';
import { InvoiceStatus } from '@models/next/StatusFlows';
import { User } from '@models/User';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { url } from '@utils/pages';
import { formatDate, includes } from '@utils/types';
import clsx from 'clsx';
import Color from 'color';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    ...common.whitespacePre,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  cardGrid: {
    columnGap: '8.5px',
    display: 'grid',
    gridAutoRows: '125px',
    gridTemplateColumns: '20fr 13fr 14fr 14fr 14fr',
    rowGap: '10px',
  },
  cardGrid2: {
    columnGap: '10px',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    rowGap: '10px',
  },
  subGrid: {
    display: 'flex',
    columnGap: '10px',
    flex: 1,
  },
  card: {
    ...common.flexColumn,
    borderRadius: '6px',
    padding: '10px 20px',
    background: '#FAFAF8',
  },
  card2: {
    background: '#FAFAF8',
    borderRadius: '6px',
    gridColumnEnd: 3,
    gridColumnStart: 1,
    gridRowEnd: 3,
    gridRowStart: 1,
    padding: '10px',
    rowGap: '10px',
    ...common.flexColumn,
  },
  card3: {
    ...common.flexColumn,
    background: '#FAFAF8',
    borderRadius: '6px',
    gridColumnStart: 3,
    gridRowEnd: 4,
    gridRowStart: 1,
    padding: '10px',
  },
  innerCard: {
    flex: 1,
    background: theme.palette.background.paper,
    borderRadius: '4px',
    padding: '10px',
  },
  cardContent: {
    ...common.flexCenter,
    justifyContent: 'space-between',
    columnGap: '18px',
  },
  cardColumn: {
    ...common.flex1,
    ...common.flexColumn,
    justifyContent: 'start',
    alignItems: 'center',
  },
  digit: {
    fontSize: '32px',
    fontWeight: 700,
    lineHeight: '48px',
    textDecorationLine: 'underline',
  },
  label: {
    ...common.middleText,
    padding: '3px 6px',
    borderRadius: '4px',
    width: 'fit-content',
  },
  scheduleTable: {
    ...common.smallerText,
    ...common.textGray700,
    lineHeight: '18px',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, auto)',
    rowGap: '3px',
  },
  deliveryRequestTable: {
    display: 'grid',
    padding: '10px',
    gridTemplateColumns: 'repeat(3, auto)',
    rowGap: '10px',
    gridAutoRows: '21px',
  },
  noResultText: {
    ...common.smallerText,
    ...common.textGray700,
    ...common.flexCenter,
    gridColumnStart: 1,
    gridColumnEnd: 4,
    gridRowStart: 2,
    gridRowEnd: 4,
    height: '3rem',
  },
  '@keyframes shine': {
    to: {
      'background-position-x': '-200%',
    },
  },
  loading: {
    background: 'linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%)',
    borderRadius: '5px',
    backgroundSize: '200% 100%',
    animation: '1s $shine linear infinite',
  },
  primary: {
    background: Color(theme.palette.primary.main).alpha(0.05).toString(),
    color: theme.palette.primary.main,
  },
  secondary: {
    background: '#EAF0F0',
    color: '#2D6866',
  },
  brown: {
    background: Color('#774202').alpha(0.05).toString(),
    color: '#774202',
  },
  orange: {
    background: Color('#F08701').alpha(0.1).toString(),
    color: '#F08701',
  },
  blue: {
    background: Color('#5177FF').alpha(0.1).toString(),
    color: '#5177FF',
  },
  grey: {
    color: theme.palette.grey[700],
    background: theme.palette.grey[200],
  },
}));

const mapStatisticalData = (
  data: Overview | undefined,
  type: 'd2d' | 'kaijo',
  dateInfo: {
    today: string;
    today_add_1_workday: string;
    today_add_2_workday: string;
  },
  user?: User | null,
) => {
  const today = dateInfo.today;
  const today_add_1_workday = dateInfo.today_add_1_workday;
  const today_add_2_workday = dateInfo.today_add_2_workday;

  const customsStatusParamsString =
    CustomsStatusName.options
      .filter((s) => s !== CustomsStatusName.enum.輸入許可)
      .map((s) => `customs_status=${s}`)
      .join('&') + '&customs_status=-';

  const serviceParamsString = (type === 'd2d' ? d2dServices : kaijoServices)
    .map((s) => `service=${encodeURIComponent(s)}`)
    .join('&');

  const moveOutParamsString = MoveOutStatusName.options
    .filter(
      (s) =>
        !includes(
          [MoveOutStatusName.enum['搬出手配済'], MoveOutStatusName.enum['搬出手配不要']],
          s,
        ),
    )
    .map((s) => `move_out_status=${s}`)
    .join('&');

  const doParamsString = DOStatusName.options
    .filter((s) => !includes([DOStatusName.enum['D/O手配済'], DOStatusName.enum['D/O手配不要']], s))
    .map((s) => `do_status=${s}`)
    .join('&');

  const numberWaitingForImportDeclaration =
    (data?.import_delaration.zero_days_before_pickup ?? 0) +
    (data?.import_delaration.one_days_before_pickup ?? 0) +
    (data?.import_delaration.two_days_before_pickup ?? 0);

  return [
    {
      title: (
        <>
          <b>{type === 'd2d' ? 'D2D' : dashboardStrings.seaRapidDelivery}</b>
          {` ${dashboardStrings.waitingForImportDeclaration}：${numberWaitingForImportDeclaration}件`}
        </>
      ),
      data: [
        {
          label: dashboardStrings.zeroDaysBeforePickupDate,
          value: data?.import_delaration.zero_days_before_pickup ?? 0,
          link: `${url(
            '/admin/booking/DeliveryRequests',
          )}?pickup_date_before=${today}&salesperson_japan_id=${
            user?.id
          }&${customsStatusParamsString}&${serviceParamsString}`,
        },
        {
          label: dashboardStrings.oneDaysBeforePickupDate,
          value: data?.import_delaration.one_days_before_pickup ?? 0,
          link: `${url(
            '/admin/booking/DeliveryRequests',
          )}?pickup_date_after=${today_add_1_workday}&pickup_date_before=${today_add_1_workday}&salesperson_japan_id=${
            user?.id
          }&${customsStatusParamsString}&${serviceParamsString}`,
        },
        {
          label: dashboardStrings.twoDaysBeforePickupDate,
          value: data?.import_delaration.two_days_before_pickup ?? 0,
          link: `${url(
            '/admin/booking/DeliveryRequests',
          )}?pickup_date_after=${today_add_2_workday}&pickup_date_before=${today_add_2_workday}&salesperson_japan_id=${
            user?.id
          }&${customsStatusParamsString}&${serviceParamsString}`,
        },
      ],
    },
    {
      title: dashboardStrings.waitingForDriverInfo,
      data: [
        {
          label:
            type === 'd2d'
              ? dashboardStrings.oneDaysBeforePickupDate
              : dashboardStrings.zeroDaysBeforePickupDate,
          value: data?.driver_info.zero_days_before_pickup ?? 0,
          link: `${url('/admin/booking/DeliveryRequests')}?pickup_date_before=${
            type === 'd2d' ? today_add_1_workday : today
          }&salesperson_japan_id=${user?.id}&driver_info_status=未&${serviceParamsString}`,
        },
      ],
    },
    {
      title: dashboardStrings.doAndMoveOut,
      data: [
        {
          label: dashboardStrings.doNoNotGetYet,
          value: data?.do ?? 0,
          link: `${url(
            '/admin/booking/CurrentBookingsAfterDeparture',
          )}?eta_before=${today}&salesperson_japan_id=${
            user?.id
          }&${doParamsString}&${serviceParamsString}`,
        },
        {
          label: dashboardStrings.moveOutNotYet,
          value: data?.move_out ?? 0,
          link: `${url(
            '/admin/booking/DeliveryRequests',
          )}?pickup_date_before=${today_add_2_workday}&salesperson_japan_id=${
            user?.id
          }&pickup_date_after=${today_add_2_workday}&${moveOutParamsString}&${serviceParamsString}`,
        },
      ],
    },
    {
      title: dashboardStrings.customs,
      data: [
        {
          label: dashboardStrings.customsRequestNotYet,
          value: data?.customs_request ?? 0,
          link: `${url(
            '/admin/booking/CurrentBookingsAfterDeparture',
          )}?eta_before=${today}&salesperson_japan_id=${user?.id}&customs_instruction_status=${
            InstructionStatusName.enum.送信済
          }&customs_request_status=-&${serviceParamsString}`,
        },
        {
          label: dashboardStrings.customerInstructionNotYet,
          value: data?.customs_instruction ?? 0,
          link: `${url(
            '/admin/booking/CurrentBookingsAfterDeparture',
          )}?etd_before=${today}&salesperson_japan_id=${user?.id}&customs_instruction_status=${
            InstructionStatusName.enum.未送信
          }&customs_instruction_status=${
            InstructionStatusName.enum.修正依頼済
          }&${serviceParamsString}`,
        },
      ],
    },
    {
      title: dashboardStrings.delivery,
      data: [
        {
          label: dashboardStrings.deliveryRequestNotYet,
          value: data?.delivery_request ?? 0,
          link: `${url(
            '/admin/booking/CurrentBookingsAfterDeparture',
          )}?eta_before=${today}&salesperson_japan_id=${user?.id}&delivery_instruction_status=${
            InstructionStatusName.enum.送信済
          }&delivery_request_status=-&${serviceParamsString}`,
        },
        {
          label: dashboardStrings.customerInstructionNotYet,
          value: data?.delivery_instruction ?? 0,
          link: `${url(
            '/admin/booking/CurrentBookingsAfterDeparture',
          )}?etd_before=${today}&salesperson_japan_id=${user?.id}&delivery_instruction_status=${
            InstructionStatusName.enum.未送信
          }&delivery_instruction_status=${
            InstructionStatusName.enum.修正依頼済
          }&${serviceParamsString}`,
        },
      ],
    },
  ];
};

interface CalculatedWorkday {
  todaySubtract1Workday: undefined | string;
  todaySubtract2Workday: undefined | string;
  todayAdd1Workday: undefined | string;
  todayAdd2Workday: undefined | string;
}

export const Dashboard: React.FC = () => {
  const classes = { ...useStyles(), ...useCommonStyles() };
  const history = useHistory();
  const today = dayjs().format('YYYY-MM-DD');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const user = useUser();
  const [calculatedWorkday, setCalculatedWorkday] = useState<CalculatedWorkday>();

  const calculateWorkday = workdayApis.usePost('/workday');

  useEffect(() => {
    const calculateWorkdayOnce = async () => {
      const workdayResults = await calculateWorkday.mutateAsync([
        {
          base_date: today,
          diff_days: -1,
        },
        {
          base_date: today,
          diff_days: -2,
        },
        {
          base_date: today,
          diff_days: 1,
        },
        {
          base_date: today,
          diff_days: 2,
        },
      ]);
      setCalculatedWorkday({
        todaySubtract1Workday: workdayResults[0],
        todaySubtract2Workday: workdayResults[1],
        todayAdd1Workday: workdayResults[2],
        todayAdd2Workday: workdayResults[3],
      });
    };
    calculateWorkdayOnce();
  }, []);

  const overviewD2d = overviewApis.useGet('/dashboard/overview', {
    queries: {
      service: d2dServices,
    },
  });

  const overviewKaijo = overviewApis.useGet('/dashboard/overview', {
    queries: {
      service: kaijoServices,
    },
  });

  const vesselSchedulesSelectedEtd = dashboardVesselScheduleApis.useGet(
    '/dashboard/vessel-schedule',
    {
      queries: {
        etd_before: dayjs(selectedDate).format('YYYY-MM-DD'),
        etd_after: dayjs(selectedDate).format('YYYY-MM-DD'),
      },
    },
  );

  const preAlertCountSelectedEtd = dashboardPreAlertApis.useGet('/dashboard/pre-alert/count', {
    queries: {
      query: {
        etd_before: dayjs(selectedDate).format('YYYY-MM-DD'),
        etd_after: dayjs(selectedDate).format('YYYY-MM-DD'),
        type: [PreAlertType.Enum.current],
      },
    },
  });

  const deliveryRequestsNoImportDeclaration = dashboardDeliveryRequestApis.useGet(
    '/dashboard/delivery-request',
    {
      queries: {
        pickup_date_before: today,
        pickup_date_after: today,
        customs_status: [CustomsStatusName.enum.税関検査],
      },
    },
  );

  const preAlertCountNoSalespersonJapan = dashboardPreAlertApis.useGet(
    '/dashboard/pre-alert/count',
    {
      queries: {
        query: {
          etd_before: dayjs(selectedDate).format('YYYY-MM-DD'),
          etd_after: dayjs(selectedDate).format('YYYY-MM-DD'),
          has_salesperson_japan: false,
          type: [PreAlertType.Enum.current],
          before_departure: true,
        },
      },
    },
  );

  const preAlertCountNoInvoice = dashboardPreAlertApis.useGet(
    '/dashboard/pre-alert/count',
    {
      queries: {
        query: {
          before_departure: false,
          type: [PreAlertType.Enum.current],
          invoice_status: InvoiceStatus.enum.未作成,
          salesperson_japan_id: user.data?.id,
          delivery_request_delivery_date_before: calculatedWorkday?.todaySubtract2Workday,
        },
      },
    },
    {
      enabled: !!calculatedWorkday?.todaySubtract2Workday,
    },
  );

  const preAlertCountAll = dashboardPreAlertApis.useGet(
    '/dashboard/pre-alert/count',
    {
      queries: {
        query: {
          before_departure: false,
          type: [PreAlertType.Enum.current],
          salesperson_japan_id: user.data?.id,
          delivery_request_delivery_date_before: calculatedWorkday?.todaySubtract2Workday,
        },
      },
    },
    {
      enabled: !!calculatedWorkday?.todaySubtract2Workday,
    },
  );

  const invoiceCountOverdue = dashboardInvoiceApis.useGet(
    '/dashboard/invoice/count',
    {
      queries: {
        charge_pay_day_before: calculatedWorkday?.todaySubtract1Workday,
        payment_status: '未',
      },
    },
    {
      enabled: !!calculatedWorkday?.todaySubtract1Workday,
    },
  );

  const invoiceCountAll = dashboardInvoiceApis.useGet(
    '/dashboard/invoice/count',
    {
      queries: {
        payment_status: '未',
      },
    },
    {
      enabled: !!calculatedWorkday?.todaySubtract1Workday,
    },
  );

  const dateInfo = {
    today,
    today_add_1_workday: calculatedWorkday?.todayAdd1Workday!,
    today_add_2_workday: calculatedWorkday?.todayAdd2Workday!,
  };

  const kaijoData = useMemo(
    () => mapStatisticalData(overviewKaijo.data, 'kaijo', dateInfo, user.data),
    [overviewKaijo.data, calculatedWorkday],
  );

  const d2dData = useMemo(
    () => mapStatisticalData(overviewD2d.data, 'd2d', dateInfo, user.data),
    [overviewD2d.data, calculatedWorkday],
  );

  const renderScheduleData = () => (
    <QueryContainer
      queries={[
        preAlertCountSelectedEtd,
        preAlertCountNoSalespersonJapan,
        vesselSchedulesSelectedEtd,
      ]}
      style={{ height: '100%' }}
    >
      <div className={clsx(classes.flexInline, classes.justifyStart, classes.itemsCenter)}>
        <div className={clsx(classes.label, classes.primary)}>ETD: </div>
        <div style={{ width: '10px' }} />
        <div className={clsx(classes.textGray700, classes.middleText)}>
          {formatDate(selectedDate.toDateString())}
        </div>
      </div>

      <div style={{ height: '10px' }} />

      <div className={clsx(classes.flexInline, classes.justifyStart, classes.itemsCenter)}>
        <div className={clsx(classes.label, classes.orange)}>{dashboardStrings.number}: </div>
        <div style={{ width: '10px' }} />
        <div className={clsx(classes.textGray700, classes.middleText)}>
          {preAlertCountSelectedEtd.data?.count}件
        </div>
        <div className={classes.flex1} />
        <div className={clsx(classes.label, classes.blue)}>{dashboardStrings.waitingForCS}: </div>
        <div style={{ width: '10px' }} />
        <div
          className={clsx(classes.link, classes.middleText, classes.underline)}
          onClick={() =>
            history.push(
              `${url('/admin/booking/CurrentBookingsBeforeDeparture')}?etd_before=${dayjs(
                selectedDate,
              ).format('YYYY-MM-DD')}&etd_after=${dayjs(selectedDate).format(
                'YYYY-MM-DD',
              )}&has_salesperson_japan=false`,
            )
          }
        >
          {preAlertCountNoSalespersonJapan.data?.count}件
        </div>
        <div style={{ width: '18px' }} />
      </div>

      <div style={{ height: '10px' }} />

      <div className={clsx(classes.label, classes.grey)}>{dashboardStrings.schedule}: </div>

      <div style={{ height: '5px' }} />

      <div className={classes.scheduleTable}>
        {vesselSchedulesSelectedEtd.data?.results?.length === 0 ? (
          <div className={classes.noResultText}>{dashboardStrings.noResult}</div>
        ) : (
          <>
            {vesselSchedulesSelectedEtd.data?.results.slice(0, 5).map((item, index) => (
              <>
                <div key={`vessel_${index}`}>{item.vessel || '-'}</div>
                <div key={`pod_${index}`}>{item.pod || '-'}</div>
                <div key={`eta_${index}`}>{formatDate(item.eta) || '-'}</div>
              </>
            ))}
            {(vesselSchedulesSelectedEtd.data?.count ?? 0) > 5 && (
              <div
                className={clsx(classes.baseButton, classes.underline)}
                onClick={() => {
                  history.push(
                    `${url('/admin/widgets/VesselSchedule')}?etd_before=${dayjs(
                      selectedDate,
                    ).format('YYYY-MM-DD')}&etd_after=${dayjs(selectedDate).format('YYYY-MM-DD')}`,
                  );
                }}
              >
                {dashboardStrings.seeMore}
              </div>
            )}
          </>
        )}
      </div>
    </QueryContainer>
  );

  const renderDeliveryRequestTable = () => (
    <QueryContainer queries={[deliveryRequestsNoImportDeclaration]} style={{ height: '100%' }}>
      <div className={classes.deliveryRequestTable}>
        <div>NO</div>
        <div>{dashboardStrings.d2dNo}</div>
        <div>CNEE</div>

        {deliveryRequestsNoImportDeclaration.data?.results.length === 0 ? (
          <div className={classes.noResultText}>{dashboardStrings.noResult}</div>
        ) : (
          <>
            {deliveryRequestsNoImportDeclaration.data?.results.map((item, index) => (
              <>
                <div className={classes.textGray700} key={`id_${index}`}>
                  {index + 1}
                </div>
                <div className={classes.textGray700} key={`d2d_${index}`}>
                  {item.booking_inquiry.d2d_no}{' '}
                  <a
                    className={clsx(classes.underline, classes.baseButton, classes.brown)}
                    href={`${url('/admin/booking/BookingDetails')}?d2dNo=${
                      item.booking_inquiry.d2d_no
                    }&type=current`}
                  >
                    {dashboardStrings.detail}
                  </a>
                </div>
                <div className={classes.textGray700} key={`name_${index}`}>
                  {item.booking_inquiry.consignee?.english_name || '-'}
                </div>
              </>
            ))}
            <div
              className={clsx(classes.baseButton, classes.underline)}
              onClick={() => {
                history.push(
                  `${url(
                    '/admin/booking/DeliveryRequests',
                  )}?pickup_date_before=${today}&pickup_date_after=${today}&customs_status=${
                    CustomsStatusName.enum.税関検査
                  }`,
                );
              }}
            >
              {dashboardStrings.seeDetail}
            </div>
          </>
        )}
      </div>
    </QueryContainer>
  );

  return (
    <Layout title={dashboardStrings.title!} loading={user.isLoading || calculateWorkday.isLoading}>
      <div className={classes.container}>
        <SectionTitle>{dashboardStrings.title}</SectionTitle>
        <div className={classes.cardGrid}>
          {kaijoData.map((item, index) => (
            <Card
              loading={overviewD2d.isLoading}
              key={index}
              title={item.title}
              data={item.data}
              color="secondary"
            />
          ))}
          {d2dData.map((item, index) => (
            <Card
              loading={overviewKaijo.isLoading}
              key={index + 5}
              title={item.title}
              data={item.data}
              color="primary"
            />
          ))}
        </div>
        <div style={{ height: '10px' }} />
        <div className={classes.cardGrid2}>
          <div className={classes.card2}>
            {dashboardStrings.schedule}
            <div className={classes.subGrid}>
              <div className={classes.innerCard}>
                <SmallCalendar date={selectedDate} onClick={(date) => setSelectedDate(date)} />
              </div>
              <div className={classes.innerCard}>{renderScheduleData()}</div>
            </div>
          </div>
          <div className={clsx(classes.card3, classes.brown)}>
            {dashboardStrings.casesInCheckOnPickupDate}：
            {deliveryRequestsNoImportDeclaration.data?.count}件
            <div style={{ height: '10px' }} />
            <div className={clsx(classes.innerCard, classes.middleText)}>
              {renderDeliveryRequestTable()}
            </div>
          </div>
          <div className={classes.card}>
            {dashboardStrings.invoice}
            <div style={{ height: '10px' }} />
            <div className={classes.subGrid}>
              <div className={clsx(classes.innerCard, classes.flexCenter)}>
                <CircularProgressBar
                  color="orange"
                  text={dashboardStrings.paymentOverdue}
                  number={invoiceCountOverdue.data?.count || 0}
                  dominator={invoiceCountAll.data?.count || 0}
                  link={`${url('/admin/invoice/InvoiceList')}?
                  invoice_status=${InvoiceStatus.enum.未作成}
                  &charge_pay_day_before=${calculatedWorkday?.todaySubtract1Workday}`}
                />
              </div>
              <div className={clsx(classes.innerCard, classes.flexCenter)}>
                <CircularProgressBar
                  text={dashboardStrings.notCreated}
                  number={preAlertCountNoInvoice.data?.count || 0}
                  dominator={preAlertCountAll.data?.count || 0}
                  link={`${url('/admin/booking/CurrentBookingsAfterDeparture')}?invoice_status=${
                    InvoiceStatus.enum.未作成
                  }&salesperson_japan_id=${user.data?.id}&delivery_request_delivery_date_before=${
                    calculatedWorkday?.todaySubtract2Workday
                  }`}
                />
              </div>
            </div>
          </div>
          <div className={classes.card}>
            {dashboardStrings.preparing}
            <div style={{ height: '10px' }} />
            <div className={classes.subGrid}>
              {[0, 1].map((_, index) => (
                <div
                  key={index}
                  className={clsx(
                    classes.innerCard,
                    classes.flexCenter,
                    classes.smallerText,
                    classes.textGray700,
                  )}
                >
                  {dashboardStrings.thisPartIsInPreparation}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

interface CardProps {
  title: React.ReactNode;
  data: { label: string; value: number; link?: string }[];
  color?: 'primary' | 'secondary';
  loading?: boolean;
}

const Card: React.FC<CardProps> = (props) => {
  const { title, data, color = 'primary', loading = false } = props;
  const classes = { ...useStyles(), ...useCommonStyles() };
  const history = useHistory();

  return (
    <div
      className={clsx(
        classes.card,
        color === 'primary' && !loading && classes.primary,
        color === 'secondary' && !loading && classes.secondary,
        loading && classes.loading,
      )}
    >
      {loading ? null : (
        <>
          <div>{title}</div>
          <div style={{ height: '10px' }} />
          <div className={classes.cardContent}>
            {data.map((item, index) => (
              <div className={classes.cardColumn} key={index}>
                <div
                  className={clsx(classes.digit, classes.baseButton)}
                  onClick={() => history.push(item.link ?? '')}
                >
                  {item.value}
                </div>
                <div style={{ height: '5px' }} />
                <div className={clsx(classes.textGray700, classes.smallerText)}>{item.label}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
